export interface Product {
  id: string;
  name: string;
  price: string;
  typeCode: string;
  isSucceed: string;
}

export interface Voice {
  id: string;
  voiceName: string;
  gender: string;
  sampleUrl: string;
  voiceCode?: string;
  language?: string;
}

export interface PublicMessage {
  id: string;
  username: string;
  content: string;
  status: "pending" | "replied" | "ignored" | "skipped" | "error" | "success";
  aiReply?: string;
  commentId?: string | null;
  isSystemMessage?: boolean; // 标识是否为系统消息
}

export interface QueueItem {
  id: string;
  text: string;
  playedCharCount?: number;
  commentId?: string;
  audioUrl?: string;
  status?: "played" | "playing" | "pending";
  // 新增：支持紧急插播和时间排序
  voiceNo?: number; // -1 表示紧急插播
  createTime?: number; // 时间戳，用于排序
  // 新增：切换版本号，隔离旧产品的残留音频
  version?: number;
  productId?: string;
}

// 导入高性能队列类
import type { HighPerformanceAudioQueue } from "@/utils/HighPerformanceAudioQueue";
import { reactive } from "vue";

export interface ProductList {
  id: string;
  name: string;
  products: Product[];
  status: string;
  language?: string;
  languageZh?: string;
  voiceProfileCode?: string;
  voiceId?: string;
  promotions?: Product[]; // 添加promotions属性，用于支持API返回的数据结构
  liveUrl?: string; // 添加liveUrl属性
  intervalTime?: number; // 🚨 新增：轮播时间间隔（从直播间列表页获取）
}

// 为单个直播间创建默认状态的辅助函数
const getDefaultRoomState = () => ({
  isInitialized: false,
  cardLoadingMap: reactive({}),
  bindLinkLoading: false,
  sendMessageLoading: false,
  saveConfigLoading: false,
  startLiveLoading: false,
  endLiveLoading: false,
  simulateCommentLoading: false,
  productList: null,
  isBound: false,
  isEditing: false,
  liveRoomRef: null,
  selectedProductId: "",
  productDetail: {},
  sessionId: "",
  roomId: "",
  externalRoomId: "",
  commentWs: null,
  audioQueueWs: null,
  audioQueueWsNoDataTimer: null,
  isConnecting: false,
  publicMessages: [],
  publicInfoRef: null,
  isNearBottom: true,
  queue: [],
  // 新增：高性能队列实例
  highPerformanceQueue: null as HighPerformanceAudioQueue | null,
  currentIndex: 0,
  // 新增：产品切换版本栅栏
  switchVersion: 0,
  queueRef: null,
  voiceLangs: [],
  voiceLang: "0",
  voiceGender: "male",
  voiceList: [],
  selectedVoiceId: null,
  selectedVoice: null,
  currentVoicePage: 1,
  voicePageSize: 6,
  activeLanguageIdx: 0,
  roomLink: "",
  livePlatform: "tiktok",
  platforms: ["抖音", "tiktok"],
  sendMsg: "",
  selectedProductIds: [],
  pendingProductSwitch: null, // 初始化待切换产品状态
  lastProductSwitchTime: null, // 新增：产品切换时间戳
  switchingProductId: null, // 新增：正在切换的产品ID
  isPlaying: false,
  lastSaveTime: null,
  lastLogTime: {},
  wasPlayingBeforeHidden: false,
  currentTimeBeforeHidden: undefined,
  commentWsReconnectCount: 0,
  audioQueueWsReconnectCount: 0,
  needsPowerSaveRecovery: false,
  carouselInterval: "", // 轮播间隔时间输入框的值
  isCarouselPlaying: false, // 轮播是否正在播放的状态
  currentDashbordForm: {}
});

export interface RoomState {
  currentDashbordForm: any;
  roomId?: string;
  queue: QueueItem[];
  // 新增：高性能队列实例
  highPerformanceQueue?: HighPerformanceAudioQueue | null;
  currentIndex: number;
  currentAudioTime?: number; // 保留向后兼容，但将逐步替换为currentAudioState
  currentAudioState?: {
    // 新增：更精确的当前音频状态
    audioId: string;
    currentTime: number;
    duration: number;
  };
  isPlaying: boolean;
  nextAudioRequested?: boolean;
  needsPowerSaveRecovery?: boolean;
  earlySwitch?: boolean; // 98%提前切换标志
  precisionMonitoring?: boolean; // 精确时机监控标志
  // 添加其他缺失的属性
  bindLinkLoading?: boolean;
  sendMessageLoading?: boolean;
  saveConfigLoading?: boolean;
  startLiveLoading?: boolean;
  endLiveLoading?: boolean;
  simulateCommentLoading?: boolean;
  isBound?: boolean;
  isEditing?: boolean;
  liveRoomRef?: any;
  publicInfoRef?: any;
  queueRef?: any;
  voiceLangs?: any[];
  voiceLang?: string;
  voiceGender?: string;
  voiceList?: Voice[];
  selectedVoiceId?: string;
  selectedVoice?: Voice;
  currentVoicePage?: number;
  voicePageSize?: number;
  activeLanguageIdx?: number;
  roomLink?: string;
  livePlatform?: string;
  platforms?: string[];
  sendMsg?: string;
  wasPlayingBeforeHidden?: boolean;
  currentTimeBeforeHidden?: number;
  lastRecoveryTime?: number; // 添加恢复时间记录
  isManuallyUnbound?: boolean; // 是否手动解绑WebSocket
  isConnecting?: boolean; // 是否正在连接中
  // WebSocket相关属性
  commentWs?: WebSocket | null;
  commentWsHeartbeatTimer?: NodeJS.Timeout | null;
  audioQueueWs?: WebSocket | null;
  audioQueueWsNoDataTimer?: NodeJS.Timeout | null;
  commentWsReconnectTimer?: NodeJS.Timeout | null;
  audioQueueWsReconnectTimer?: NodeJS.Timeout | null;
  commentWsReconnectCount?: number;
  audioQueueWsReconnectCount?: number;
  sessionId?: string;
  publicMessages?: PublicMessage[];
  productList?: ProductList | null;
  needsReconnect?: boolean; // 标记是否需要重新连接WebSocket
  isPageRefreshed?: boolean; // 标记页面是否刷新过，防止列表页自动恢复功能
  readyToRestore?: boolean; // 新增：标记是否准备好恢复播放（用于页面刷新后的恢复协调）
  // 移除去重保护相关字段，让每个后端推送的音频都能正常播放
  // 其他缺失的属性
  isInitialized?: boolean;
  selectedProductId?: string;
  productDetail?: any;
  selectedProductIds?: string[];
  // 新增：待切换的产品信息
  pendingProductSwitch?: {
    id: string;
    index: number;
  } | null;
  // 产品切换相关
  switchingProductId?: string | null;
  lastProductSwitchTime?: number | null;
  lastLogTime?: any;
  cardLoadingMap?: any;
  isPreloading?: boolean; // 是否正在预加载
  // 新增：产品切换版本栅栏
  switchVersion?: number;
  // 轮播间隔时间输入框的值
  carouselInterval?: string;
  // 轮播是否正在播放的状态
  isCarouselPlaying?: boolean;
  // 新增：体验咨询消息列表
  experienceAskMessageList?: any[];
  // 插播列表
  insertSegmentList?: any[];
  // 新增：直播风格
  liveStyle?: any;
  isNearBottom?: boolean;
  // 新增：音频队列WebSocket错误重连计数器
  audioQueueWsErrorReconnectCount?: number;
}

// 全局状态类型定义
export interface GlobalState {
  // WebSocket 和网络状态
  showProductSelector: boolean;
  products: any[];

  // 简化：保留用户交互状态但不用于权限检查
  userHasInteracted: boolean;
  globalInteractionSetup: boolean;
  // 移除未使用的字段：activateAllRoomsHandler, overlayElement, permissionPromiseResolve, permissionResolve

  // 音频激活相关
  pendingActivationQueue: string[];
  isAutoplayActivatorRunning: boolean;
  autoplayPolicyEnabled: boolean;

  // 保存原始商品状态，用于取消时恢复
  _originalProductState: any;

  // 开播前预检查：预览播放器状态（全局）
  previewAudioActive: boolean;
  previewAudioIsPlaying: boolean;
  previewAudioLastEvent?: string;
}

export interface RootState extends GlobalState {
  // 房间状态管理
  roomStates: Map<string, RoomState>;
  currentRoomId: string | null;
}

export const state = (): RootState => ({
  // 房间状态管理
  roomStates: new Map(),
  currentRoomId: null,

  // 商品选择器相关的状态是全局的，因为它是一个模态框
  showProductSelector: false,
  products: [],

  // 保存原始商品状态，用于取消时恢复
  _originalProductState: null,

  // 🔇 全局用户交互监听器状态
  globalInteractionSetup: false,
  // 移除未使用的字段：activateAllRoomsHandler, overlayElement

  // 🔇 用户交互状态：用于控制音频是否可以有声播放
  userHasInteracted: false,

  // 🚀 待激活播放队列
  pendingActivationQueue: [] as string[],
  isAutoplayActivatorRunning: false,
  autoplayPolicyEnabled: false,

  // 移除未使用的权限相关字段：permissionPromiseResolve, permissionResolve
  // 预检查：预览播放器全局状态
  previewAudioActive: false,
  previewAudioIsPlaying: false,
  previewAudioLastEvent: undefined
});

export { getDefaultRoomState };
